/**
 * monetization/quota — evaluación PURA de quotas (uso vs límites) para la UI.
 *
 * El enforcement DURO vive en el server (write-path Rust + upload); esto es la
 * capa de preflight/presentación: barras de uso, aviso de "cerca del límite" y
 * deshabilitar creates cuando el tier está lleno, para que el usuario nunca
 * choque a ciegas contra el 402/rechazo del server.
 */

import type { QuotaEvaluation, QuotaKey, QuotaLimits, UsageSnapshot } from "./types";
import { QUOTA_KEYS } from "./types";

export const NEAR_LIMIT_RATIO = 0.8;

export function emptyUsage(): UsageSnapshot {
  return { storage_bytes: 0, items: 0, collections: 0, orgs: 0, attachments: 0 };
}

/** Evalúa UNA quota. */
export function evaluateQuota(
  key: QuotaKey,
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaEvaluation {
  const used = usage[key] ?? 0;
  const limit = limits[key] ?? null;
  if (limit === null) {
    return { key, used, limit: null, ratio: 0, nearLimit: false, exceeded: false };
  }
  const ratio = limit <= 0 ? 1 : Math.min(used / limit, 1);
  return {
    key,
    used,
    limit,
    ratio,
    nearLimit: ratio >= NEAR_LIMIT_RATIO,
    exceeded: used >= limit,
  };
}

/** Evalúa TODAS las quotas (orden estable de QUOTA_KEYS). */
export function evaluateQuotas(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaEvaluation[] {
  return QUOTA_KEYS.map((key) => evaluateQuota(key, limits, usage));
}

/** ¿Cabe un consumo adicional de `delta` en `key`? (preflight de creates). */
export function canConsume(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
  key: QuotaKey,
  delta = 1,
): boolean {
  const limit = limits[key] ?? null;
  if (limit === null) {
    return true;
  }
  return (usage[key] ?? 0) + delta <= limit;
}

/** Claves actualmente excedidas. */
export function exceededKeys(
  limits: Partial<QuotaLimits>,
  usage: Partial<UsageSnapshot>,
): QuotaKey[] {
  return QUOTA_KEYS.filter((key) => evaluateQuota(key, limits, usage).exceeded);
}

/** Formato compacto de bytes para las barras de uso (10.5 MB, 2 GB…). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 || Number.isInteger(value) ? Math.round(value) : value.toFixed(1);
  return `${rounded} ${units[unit]}`;
}

/** Valor mostrable de un límite (`null` ⇒ "∞"). */
export function formatLimit(key: QuotaKey, limit: number | null): string {
  if (limit === null) {
    return "∞";
  }
  return key === "storage_bytes" ? formatBytes(limit) : String(limit);
}

/** Valor mostrable del uso. */
export function formatUsed(key: QuotaKey, used: number): string {
  return key === "storage_bytes" ? formatBytes(used) : String(used);
}
